import React, { useEffect, createContext } from 'react';
import { useAppState } from 'oasis-os-react';
import { getParam } from 'oasis-os-utils';
import { updateDomainUrl, getOasisConfigOverrides, updateContentfulOverrides, updateOasisConfigOverrides, } from './utils';
import { createClient } from './client';
import { useEntry, useEntries } from './hooks';
import { cloneDeep } from 'lodash-es';
/**
 * Enum for the url query parameters
 */
export var UrlQueryParams;
(function (UrlQueryParams) {
    UrlQueryParams["CONTENT_ID"] = "contentId";
    UrlQueryParams["SPACE_ID"] = "spaceId";
    UrlQueryParams["ENVIRONMENT"] = "environment";
    UrlQueryParams["PREVIEW_MODE"] = "preview";
})(UrlQueryParams || (UrlQueryParams = {}));
export const ClientContext = createContext({
    context: null,
    callback: () => { },
});
export const AppStateProvider = ({ id, query, config, additionalConfig, children, tenant, }) => {
    const [appState, setAppState, app] = useAppState();
    const contentId = id || getParam(UrlQueryParams.CONTENT_ID) || process.env.CONTENT_ID;
    const { releaseId, domainName, isHybrid, localApps, localAppConfig } = additionalConfig || {};
    const localAppsList = localApps ? localApps.split(',') : [];
    app.meta = { ...app.meta, contentId, query };
    app.config = {
        ...app.config,
        CONTENTFUL_SPACE: config?.spaceId || process.env.CONTENTFUL_SPACE,
        CONTENTFUL_TOKEN: config?.token || process.env.CONTENTFUL_TOKEN,
        CONTENTFUL_ENVIRONMENT: config?.environment || process.env.CONTENTFUL_ENVIRONMENT,
        CONTENTFUL_RELEASE_ID: config?.releaseId || process.env.CONTENTFUL_RELEASE_ID,
        APP_ID: config?.appId || process.env.APP_ID,
        CLIENT_ID: config?.clientId || process.env.CLIENT_ID,
    };
    let entry = null;
    let entryError;
    let releaseEntry = null;
    let releaseEntryError;
    /**
     * Call both hooks so as not to violate hook rules
     * The hook provided with false-ish data will return nothing.
     * if app state already exists, it was forwarded from the parent
     */
    if (!isHybrid || (!localAppConfig && !tenant)) {
        [entry, entryError] = useEntry(appState && !!appState.sys ? null : contentId, query);
    }
    const [entryCollection, entryCollectionError] = useEntries(contentId ? null : query);
    /**
     * Call both hooks so as not to violate hook rules.
     * The hook provided with false-ish data will return nothing.
     * if app state already exists, it was forwarded from the parent
     */
    if (app.config.CONTENTFUL_RELEASE_ID && !releaseEntry && tenant) {
        [releaseEntry, releaseEntryError] = useEntry(app.config.CONTENTFUL_RELEASE_ID, query);
    }
    else {
        [releaseEntry, releaseEntryError] = useEntry(appState && !!appState.sys ? null : releaseId, query);
    }
    /**
     * When data from Contentful is fetched then save it to app's state
     * Check for the entry and release entry
     */
    const handleEntryUpdates = (tenant) => {
        app.config = {
            ...app.config,
            APP_ID: config?.appId || process.env.APP_ID,
            CLIENT_ID: config?.clientId || process.env.CLIENT_ID || tenant,
        };
        if (!entry && isHybrid && localAppConfig) {
            entry = localAppConfig;
        }
        if (!entry && releaseEntry) {
            entry = appState;
        }
        if (entry && releaseEntry) {
            const contentfulEnvironment = window?.location?.host.includes('localhost')
                ? 'local'
                : app.config.CONTENTFUL_ENVIRONMENT;
            const featureApps = releaseEntry?.fields?.releases?.[0]?.fields
                ?.features || undefined;
            const entryFields = entry?.fields;
            const releaseFields = releaseEntry?.fields;
            if (entryFields && releaseFields) {
                /**
                 * Get the override oasis app config for environments and clients
                 */
                const oasisConfigOverrides = getOasisConfigOverrides(entryFields?.config?.fields?.env === contentfulEnvironment &&
                    !app.config.CLIENT_ID, entryFields?.config?.fields?.envConfig, releaseFields?.clientOverrides, app.config.CLIENT_ID, app.config.APP_ID, contentfulEnvironment);
                /**
                 * Apply the overrides to oasis App config if there is any
                 */
                let contentfulConfig = oasisConfigOverrides?.fields
                    ? updateOasisConfigOverrides(entry, oasisConfigOverrides?.fields)
                    : entry;
                /**
                 * Apply contentful config overrides if turned on
                 */
                if (app.config.APP_ID &&
                    app.config.CLIENT_ID &&
                    contentfulConfig?.fields?.config?.fields?.functionalConfig?.features
                        ?.overrideById?.enabled) {
                    const updatedFields = {
                        ...updateContentfulOverrides(contentfulConfig.fields, 'overrideId', releaseFields?.clientOverrides, {
                            appId: app.config.APP_ID,
                            clientId: app.config.CLIENT_ID,
                            contentfulEnvironment,
                        }),
                    };
                    // Create a new object with the updated fields
                    const updatedConfig = {
                        fields: updatedFields,
                        metadata: contentfulConfig.metadata,
                        sys: contentfulConfig.sys,
                    };
                    contentfulConfig = { ...updatedConfig };
                }
                /**
                 * Go through the final config and update the
                 * domain url (<host>:FA/remote.js)
                 */
                const processedConfig = updateDomainUrl(contentfulConfig, featureApps, 'src', localAppsList, domainName, isHybrid);
                /**
                 * Set the final config in the state of the app
                 */
                const deepCopy = (obj) => {
                    return cloneDeep(obj);
                };
                const newAppState = processedConfig ? deepCopy(processedConfig) : {};
                setAppState(newAppState);
            }
        }
        if (entryCollection) {
            setAppState(entryCollection);
        }
        if (entryError) {
            console.error(entryError);
        }
        if (releaseEntryError) {
            console.error(releaseEntryError);
        }
        if (entryCollectionError) {
            console.error(entryCollectionError);
        }
    };
    useEffect(() => {
        if (tenant) {
            handleEntryUpdates(tenant);
        }
        else {
            handleEntryUpdates();
        }
    }, [
        entry,
        entryCollection,
        entryError,
        entryCollectionError,
        releaseEntry,
        releaseEntryError,
        tenant,
    ]);
    return React.createElement(React.Fragment, null, children);
};
export const ContentfulProvider = (props) => {
    const [tenant, setTenant] = React.useState('');
    const client = React.useMemo(() => createClient(props.config), [
        props.config,
    ]);
    const callback = React.useCallback((tenantClientId) => {
        return new Promise((resolve) => {
            setTenant(tenantClientId);
            resolve();
        });
    }, []);
    const contextValue = React.useMemo(() => {
        return {
            context: client || {},
            callback: callback,
        };
    }, [client, callback]);
    return (React.createElement(ClientContext.Provider, { value: contextValue },
        React.createElement(AppStateProvider, { ...props, tenant: tenant })));
};
